import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Swords, TrendingUp, TrendingDown, AlertCircle, Gauge } from "lucide-react";
import { cn } from "@/lib/utils";

interface ConflictCosts {
  credibility?: string;
  speed?: string;
  learning_loss?: string;
  resistance?: string;
  additional_cost?: string;
}

interface ConflictSectionProps {
  strategyA?: string;
  strategyB?: string;
  costs?: ConflictCosts;
  className?: string;
}

const costLabels: Record<string, string> = {
  credibility: "Glaubwürdigkeit",
  speed: "Geschwindigkeit",
  learning_loss: "Lernverlust",
  resistance: "Interner Widerstand"
};

const getCostColor = (level: string) => {
  switch (level) {
    case "high":
      return "text-rose-600 dark:text-rose-400 bg-rose-500/10";
    case "medium":
      return "text-amber-600 dark:text-amber-400 bg-amber-500/10";
    case "low":
      return "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10";
    default:
      return "text-muted-foreground bg-muted";
  }
};

const getCostLabel = (level: string) => {
  switch (level) {
    case "high":
      return "Hoch";
    case "medium":
      return "Mittel";
    case "low":
      return "Gering";
    default:
      return "-";
  }
};

export function ConflictSection({ strategyA, strategyB, costs, className }: ConflictSectionProps) {
  const hasConflict = strategyA || strategyB;
  const hasCosts = costs && (costs.credibility || costs.speed || costs.learning_loss || costs.resistance);

  if (!hasConflict && !hasCosts) return null;

  return (
    <Card id="conflict-section" className={cn("scroll-mt-24 border-border/50", className)}>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Swords className="h-5 w-5 text-primary" />
            Zielkonflikt
          </CardTitle>
          <Badge variant="outline" className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400">
            Trade-off
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Der zentrale Zielkonflikt, der diese Entscheidung erzwingt
        </p>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Two-Pole Conflict Display */}
        {hasConflict && (
          <div className="grid gap-4 md:grid-cols-[1fr,auto,1fr]">
            {/* Strategy A - Trade-off Side */}
            {strategyA && (
              <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-full bg-amber-500/10 flex items-center justify-center">
                    <TrendingDown className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <h4 className="font-semibold text-sm text-amber-700 dark:text-amber-400">
                    Wenn wir A wählen, geben wir auf
                  </h4>
                </div>
                <p className="text-sm leading-relaxed pl-9 font-medium">
                  {strategyA}
                </p>
                <Badge variant="secondary" className="text-[10px] mt-3 ml-9">
                  Nutzereingabe
                </Badge>
              </div>
            )}

            {/* Visual Divider */}
            <div className="hidden md:flex flex-col items-center justify-center py-4">
              <div className="w-px h-6 bg-border/50" />
              <div className="w-10 h-10 rounded-full bg-muted/80 border border-border/50 flex items-center justify-center shadow-sm">
                <Swords className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="w-px h-6 bg-border/50" />
            </div>
            
            {/* Mobile Divider */}
            <div className="flex md:hidden items-center justify-center py-2">
              <div className="h-px w-12 bg-border/50" />
              <div className="w-8 h-8 rounded-full bg-muted/80 border border-border/50 flex items-center justify-center mx-3">
                <Swords className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="h-px w-12 bg-border/50" />
            </div>

            {/* Strategy B - Trade-off Side */}
            {strategyB && (
              <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-full bg-amber-500/10 flex items-center justify-center">
                    <TrendingDown className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <h4 className="font-semibold text-sm text-amber-700 dark:text-amber-400">
                    Wenn wir B wählen, geben wir auf
                  </h4>
                </div>
                <p className="text-sm leading-relaxed pl-9 font-medium">
                  {strategyB}
                </p>
                <Badge variant="secondary" className="text-[10px] mt-3 ml-9">
                  Nutzereingabe
                </Badge>
              </div>
            )}
          </div>
        )}

        {/* Cost Assessment */}
        {hasCosts && (
          <div className="pt-4 border-t border-border/50">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="h-4 w-4 text-rose-500" />
              <h4 className="font-semibold text-sm">
                Kosten der Nicht-Option
              </h4>
              <Badge variant="outline" className="text-[10px] ml-auto">
                Nutzerbewertung
              </Badge>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(["credibility", "speed", "learning_loss", "resistance"] as const).map((key) => {
                const value = costs?.[key];
                if (!value) return null;
                
                return (
                  <div 
                    key={key}
                    className="p-3 rounded-lg bg-muted/30 border border-border/30"
                  >
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Gauge className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground font-medium">
                        {costLabels[key]}
                      </span>
                    </div>
                    <span className={cn(
                      "inline-flex px-2 py-0.5 rounded text-xs font-medium",
                      getCostColor(value)
                    )}>
                      {getCostLabel(value)}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Additional Cost */}
            {costs?.additional_cost && (
              <div className="mt-3 p-3 rounded-lg bg-muted/20 border border-border/30">
                <span className="text-xs text-muted-foreground block mb-1">Weitere Kosten:</span>
                <p className="text-sm">{costs.additional_cost}</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
